import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useRef } from "react";
import { getAllTargetsForAccount, addTargetForAccount, removeTargetForAccount } from "@/utils/supabase-queries";
import supabaseClient from "@/utils/supabase-browser";
import { Table, UnwrapPromise } from "@/types";
import { isDemoAccount, fakeMutationDelay } from "@/utils/demo-helpers";
import { DEMO_TARGETS, DemoTarget } from "@/utils/demo-targeting-data";

// Convert DemoTarget to Table<"targets"> format for consistency
const mapDemoTargetToTableFormat = (demoTarget: DemoTarget, accountId: string): Table<"targets"> => ({
  id: parseInt(demoTarget.id.replace(/\D/g, '')) || Date.now(),
  account_id: accountId,
  target: demoTarget.name,
  type: demoTarget.type,
  name: demoTarget.name,
  platform: demoTarget.platform,
  platform_id: null,
  target_id: null,
  target_thumbnail: demoTarget.thumbnail,
  enabled: true,
  count: demoTarget.count,
  source: demoTarget.source,
  created_at: new Date().toISOString()
});

// Get demo targets in Table<"targets"> format
const getDemoTargetsAsTableFormat = (accountId: string): Table<"targets">[] => {
  const demoTargets = DEMO_TARGETS[accountId];
  if (!demoTargets) return [];
  return demoTargets.map(t => mapDemoTargetToTableFormat(t, accountId));
};
export type TargetsByIdData = UnwrapPromise<ReturnType<typeof useGetAllTargetsForAccount>>;
export const useGetAllTargetsForAccount = accountId => {
  const $ = _c(11);
  let t0;
  if ($[0] !== accountId) {
    t0 = accountId ? isDemoAccount(accountId) : false;
    $[0] = accountId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isDemo = t0;
  let t1;
  if ($[2] !== accountId) {
    t1 = ["targets", accountId];
    $[2] = accountId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  let t3;
  if ($[4] !== accountId || $[5] !== isDemo) {
    t2 = async () => {
      console.log("\uD83D\uDE80 ~ file: react-query-hooks.ts:useGetAllTargetsForAccount() ~ accountId", accountId);
      if (accountId) {
        if (isDemo) {
          return getDemoTargetsAsTableFormat(accountId);
        }
        return getAllTargetsForAccount(supabaseClient, accountId);
      } else {
        return [];
      }
    };
    t3 = isDemo && accountId ? getDemoTargetsAsTableFormat(accountId) : undefined;
    $[4] = accountId;
    $[5] = isDemo;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  const t4 = !!accountId;
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = {
      placeholderData: t3,
      staleTime: 300000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: t4
    };
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return useQuery(t1, t2, t5);
};
export const useAddTargetMutation = t0 => {
  const $ = _c(15);
  const {
    onMutate,
    onSuccess,
    onError
  } = t0;
  const queryClient = useQueryClient();
  const toastRef = useRef(undefined);
  let t1;
  if ($[0] !== onMutate || $[1] !== queryClient) {
    t1 = async newTarget => {
      await queryClient.cancelQueries(["targets", newTarget.accountId]);
      const previousTargets = queryClient.getQueryData(["targets", newTarget.accountId]);
      queryClient.setQueryData(["targets", newTarget.accountId], old => {
        const optimisticTarget = {
          ...newTarget.target,
          id: "temp-id-" + new Date().getTime(),
          account_id: newTarget.accountId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        return [...(old || []), optimisticTarget];
      });
      const prefix = newTarget.target.type === "hashtag" ? "#" : "@";
      toastRef.current = toast.loading(`Adding ${prefix}${newTarget.target.target}...`, {
        id: toastRef.current || ""
      });
      onMutate?.(newTarget.target);
      return {
        previousTargets
      };
    };
    $[0] = onMutate;
    $[1] = queryClient;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== onSuccess) {
    t2 = data => {
      const prefix_0 = data.type === "hashtag" ? "#" : "@";
      toast.success(`${prefix_0}${data.target} added to your targets!`, {
        id: toastRef.current || ""
      });
      onSuccess?.(data);
    };
    $[3] = onSuccess;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== onError || $[6] !== queryClient) {
    t3 = (error, variables, context) => {
      queryClient.setQueryData(["targets", variables.accountId], context.previousTargets);
      console.log("error in useAddTargetMutation: ", error);
      const prefix_1 = variables.target.type === "hashtag" ? "#" : "@";
      if ((error as any).code && (error as any).code === "23505") {
        toast.error(`${prefix_1}${variables.target.target} is already in your targets`, {
          id: toastRef.current || ""
        });
      } else {
        toast.error("Couldn't add target. Please try again.", {
          id: toastRef.current || ""
        });
      }
      toastRef.current = undefined;
      onError?.(error, variables, context);
    };
    $[5] = onError;
    $[6] = queryClient;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== queryClient) {
    t4 = (data_0, error_0, variables_0) => {
      if (!isDemoAccount(variables_0.accountId)) {
        queryClient.invalidateQueries(["targets", variables_0.accountId]);
      }
    };
    $[8] = queryClient;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t1 || $[11] !== t2 || $[12] !== t3 || $[13] !== t4) {
    t5 = {
      onMutate: t1,
      onSuccess: t2,
      onError: t3,
      onSettled: t4
    };
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return useMutation(_temp, t5);
};
export const useRemoveTargetMutation = t0 => {
  const $ = _c(16);
  const {
    onMutate,
    onSuccess,
    onError
  } = t0;
  const queryClient = useQueryClient();
  const toastRef = useRef(undefined);
  let t1;
  if ($[0] !== onMutate || $[1] !== queryClient) {
    t1 = async target => {
      onMutate?.(target.targetId);
      await queryClient.cancelQueries(["targets", target.accountId]);
      const previousTargets = queryClient.getQueryData(["targets", target.accountId]);
      queryClient.setQueryData(["targets", target.accountId], old => {
        if (!old) {
          return [];
        }
        return old.filter(t => String(t.id) !== String(target.targetId));
      });
      return {
        previousTargets
      };
    };
    $[0] = onMutate;
    $[1] = queryClient;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== onError || $[4] !== queryClient) {
    t2 = (error, variables, context) => {
      if (context?.previousTargets) {
        queryClient.setQueryData(["targets", variables.accountId], context.previousTargets);
      }
      toast.error("Couldn't remove target. Please try again.", {
        id: toastRef.current || ""
      });
      toastRef.current = undefined;
      onError?.(error, variables, context);
    };
    $[3] = onError;
    $[4] = queryClient;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== onSuccess || $[7] !== queryClient) {
    t3 = (data, variables_0) => {
      if (!isDemoAccount(variables_0.accountId)) {
        queryClient.invalidateQueries(["targets", variables_0.accountId]);
      }
      toast.success("Target removed", {
        id: toastRef.current || ""
      });
      onSuccess?.((data as any).id);
    };
    $[6] = onSuccess;
    $[7] = queryClient;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== queryClient) {
    t4 = (data_0, error_0, variables_1) => {
      if (!isDemoAccount(variables_1.accountId)) {
        queryClient.invalidateQueries(["targets"]);
      }
    };
    $[9] = queryClient;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t1 || $[12] !== t2 || $[13] !== t3 || $[14] !== t4) {
    t5 = {
      onMutate: t1,
      onError: t2,
      onSuccess: t3,
      onSettled: t4
    };
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return useMutation(_temp2, t5);
};
export const useOptimizeTargetsMutation = t0 => {
  const $ = _c(12);
  const {
    accountId,
    onSuccess,
    onError
  } = t0;
  const queryClient = useQueryClient();
  const toastRef = useRef(undefined);
  let t1;
  if ($[0] !== accountId) {
    t1 = async () => {
      if (isDemoAccount(accountId)) {
        await fakeMutationDelay();
        return {
          users: ["competitor1", "competitor2", "competitor3"],
          hashtags: ["trending1", "trending2", "trending3"],
          niche: "Fitness & Wellness"
        };
      }
      const response = await fetch("/api/ai/optimize-targets", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          accountId
        }),
        credentials: "include"
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Optimization failed");
      }
      return response.json();
    };
    $[0] = accountId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      toastRef.current = toast.loading("Finding the best targets for your niche...");
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== accountId || $[4] !== onSuccess || $[5] !== queryClient) {
    t3 = data => {
      queryClient.invalidateQueries(["targets", accountId]);
      if (data.alreadyOptimized) {
        toast.success("Your targets are already optimized!", {
          id: toastRef.current || ""
        });
      } else {
        const userCount = data.users?.length || 0;
        const hashtagCount = data.hashtags?.length || 0;
        const parts = [];
        if (userCount > 0) {
          parts.push(`${userCount} account${userCount > 1 ? "s" : ""}`);
        }
        if (hashtagCount > 0) {
          parts.push(`${hashtagCount} hashtag${hashtagCount > 1 ? "s" : ""}`);
        }
        const nicheText = data.niche ? ` in ${data.niche}` : "";
        toast.success(`Added ${parts.join(" and ")}${nicheText}`, {
          id: toastRef.current || "",
          duration: 5000
        });
      }
      toastRef.current = undefined;
      onSuccess?.();
    };
    $[3] = accountId;
    $[4] = onSuccess;
    $[5] = queryClient;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== onError) {
    t4 = error => {
      console.error("Error optimizing targets:", error);
      toast.error(error instanceof Error ? error.message : "Failed to optimize targets. Please try again.", {
        id: toastRef.current || ""
      });
      toastRef.current = undefined;
      onError?.(error);
    };
    $[7] = onError;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = {
      onMutate: t2,
      onSuccess: t3,
      onError: t4
    };
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return useMutation(t1, t5);
};
async function _temp(t0) {
  const {
    accountId,
    target
  } = t0;
  if (isDemoAccount(accountId)) {
    await fakeMutationDelay();
    return {
      ...target,
      id: Date.now(),
      account_id: accountId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      name: target.target || "",
      platform: target.platform || "instagram",
      platform_id: target.platform_id || "",
      target: target.target || "user",
      target_id: target.target_id || "",
      target_thumbnail: target.target_thumbnail || "",
      type: target.type || "user",
      enabled: target.enabled ?? true,
      count: target.count || 0
    } as Table<"targets">;
  }
  return addTargetForAccount(supabaseClient, accountId, target);
}
async function _temp2(t0) {
  const {
    accountId,
    targetId
  } = t0;
  if (isDemoAccount(accountId)) {
    await fakeMutationDelay();
    return {
      account_id: accountId,
      id: targetId
    } as any;
  }
  return removeTargetForAccount(supabaseClient, accountId, targetId);
}